import { motion, AnimatePresence } from "framer-motion";
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useNavigate,
} from 'react-router';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
  getFeatures,
} from 'web-selectors';

import {
  uiActions,
} from 'web-actions';

import {
  Flex,
} from 'web-styled';

import {
  getTourSteps,
} from './tour_data';

import {
  getStepsByNamespace,
  calculateOnboardPercentage,
} from './onboard_data';

import Tour from './Tour';
import Tables from './Tables';
import Indicators from './Indicators';
import Onboarding from './Onboarding';

const mapState = (state) => ({
  profile: coreSelectors.getProfile(state),
  features: getFeatures(state),
  allWorkout: coreSelectors.getResourceMappedList('workoutEvents', 'all')(state),
  workoutEvents: coreSelectors.getEventsGroupByBeginsAt()(state),
  missedWorkouts: coreSelectors.getMissedWorkouts()(state),
  clientsPerformance: coreSelectors.getClientsPerformance(state),
  firstWorkout: coreSelectors.getFirstWorkout(state),
  firstExerciseFolder: coreSelectors.getExerciseFolder(state),
  ...getUiState([
    'namespace',
    'openedTooltips',
  ])(state),
});

const Pro = () => {
  const navigate = useNavigate();
  const {
    profile,
    features,
    namespace,
    allWorkout,
    firstWorkout,
    workoutEvents,
    missedWorkouts,
    openedTooltips,
    firstExerciseFolder,
    clientsPerformance,
  } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();

  const allSteps = Object.keys(getStepsByNamespace(namespace.symbol, profile))
  const userSteps = profile.user_settings.tutorial_steps;
  const videoSeen = profile.user_settings.web_onboarding_video_seen;
  const percentage = calculateOnboardPercentage(namespace.symbol, profile);
  const tourSteps = getTourSteps(userSteps);
  const canShowTour = namespace.symbol !== 'ideafit' && videoSeen && tourSteps.some(s => !s.isCompleted);

  const handleUpdateSteps = (steps: string[]) => {
    dispatch(
      coreActions.requestMarkTutotialStepsComplite({
        steps,
      }),
    );
  };

  const updateTooltips = (tips: any) => {
    dispatch(
      uiActions.setUiState({
        openedTooltips: tips,
      }),
    );
  };

  const handleGoal = (current: string) => {
    switch (current) {
      case 'add_client':
        if (openedTooltips.findIndex(el => el.key === 'Clients') === -1) {
          updateTooltips(
            [
              {
                key: 'Clients',
              },
              {
                key: 'add_client',
              },
            ],
          );
        }
        break;
      case 'add_exercise':
        if (openedTooltips.findIndex(el => el.key === 'Exercises') === -1) {
          updateTooltips(
            [
              {
                key: 'Exercises',
              },
              {
                key: firstExerciseFolder?.id,
                label: 'Select a folder.',
                nextKey: 'add_exercises',
                isFolder: true,
              },
            ],
          );
        }
        break;
      case 'send_message':
        updateTooltips(
          [
            {
              key: 'send_message',
            },
          ],
        );
        navigate("/messages")
        break;
      case 'schedule_workout':
        if (openedTooltips.findIndex(el => el.key === 'schedule_workout') === -1) {
          updateTooltips(
            [
              {
                key: 'Calendar',
              },
              {
                key: 'schedule_workout_button',
              },
            ],
          );
        }
        break;
      case 'assign_workout':
        if (openedTooltips.findIndex(el => el.key === 'assign') === -1) {
          updateTooltips(
            [
              {
                key: 'Workout Templates',
              },
              {
                key: firstWorkout?.id,
                label: 'Select a Workout Template.',
              },
              {
                key: 'assign',
              },
              {
                key: 'choose_client',
              },
            ],
          );
        }
        break;
      case 'build_your_own_workout':
        if (openedTooltips.findIndex(el => el.key === 'add_workoutTemplates') === -1) {
          updateTooltips(
            [
              {
                key: 'Workout Templates',
              },
              {
                key: 'add_workoutTemplates',
              },
              {
                key: 'edit_workout_button',
              },
              {
                key: 'exercise_property',
              },
            ],
          );
        }
        break;
      case 'ideafit_perform_workout':
        if (openedTooltips.findIndex(el => el.key === 'ideafit_perform_workout') === -1) {
          updateTooltips(
            [
              {
                key: 'add_results',
              },
            ],
          );

          const last = allWorkout[allWorkout.length - 1]
          if (missedWorkouts.length) {
            navigate(`/events/${missedWorkouts[0].id}/results`)
          } else if (last) {
            navigate(`/events/${last.id}/results`)
          }
        }
        break;
      case 'review_calendar':
        if (openedTooltips.findIndex(el => el.key === 'Calendar') === -1) {
          updateTooltips(
            [
              {
                key: 'Calendar',
              },
              {
                key: 'review_calendar',
              },
            ],
          );

          if (missedWorkouts.length) {
            navigate(`/events/${missedWorkouts[0].id}/results`)
          }
        }
        break;
      case 'perform_workout':
        break;
      case 'review_documents':
        if (openedTooltips.findIndex(el => el.key === 'Documents') === -1) {
          updateTooltips(
            [
              {
                key: 'Documents',
              },
              {
                key: 'HTS Manual',
              },
              {
                key: 'view_document',
              },
            ],
          );
        }
        break;
      case 'got_questions':
        handleUpdateSteps(allSteps);
        break;
      default:
        break;
    }
  };

  return (
    <Flex
      column
      spacing={55}
      padding="41px 23px 54px"
      flex="1 1 0"
    >
      <AnimatePresence mode="wait">
        {(percentage === 100) ? (
          <motion.div
            key="first"
            style={{ width: '100%' }}
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.5 }}
          >
            <Indicators
              data={profile.dashboard || {}}
              features={features}
            />
          </motion.div>
        ) : (
          <motion.div
            key="second"
            style={{ width: '100%' }}
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.5 }}
          >
            <Onboarding
              profile={profile}
              namespace={namespace}
              percentage={percentage}
              onGoal={handleGoal}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <Tables
        features={features}
        workoutEvents={workoutEvents}
        missedWorkouts={missedWorkouts}
        clientsPerformance={clientsPerformance}
      />
      {canShowTour && (
        <Tour
          tourSteps={tourSteps}
          handleOnPrev={(step) => {
            handleUpdateSteps(userSteps.filter(s => s !== step));
          }}
          handleOnNext={(step) => {
            handleUpdateSteps([step]);
          }}
        />
      )}
    </Flex>
  );
};

export default Pro;
